import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import apiendpoints from "../apiendpoints.json";
import Thanku from "./HomePageComp/Thanku";
import useRecaptcha from "../hooks/useRecaptcha";
import CaptchaV2 from "./CaptchaV2";
import recaptchaRepository from "../repositories/recaptchaRepository";

const RECAPTCHA_ACTION = "enquiry_form_load";

const FormComponent = () => {
  const emailValidation = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const phoneValidation = /^(?:\+?\d{1,3}[ -]?)?(?:\d{3,5}[- ]?)?\d{10}$/;
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("Equity Research Mart - Enquiry");
  const [body, setBody] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [showThank, setThank] = useState(false);
  const [buttonEnable, setButton] = useState(true);

  // reCAPTCHA state
  const [captchaV2Token, setCaptchaV2Token] = useState("");
  const [showV2, setShowV2] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [errors, setErrors] = useState({});

  const captchaV2Ref = useRef(null);

  const { getV3Token, isReady: v3Ready } = useRecaptcha();

  // Run v3 verify on mount once the v3 script is ready.
  useEffect(() => {
    let cancelled = false;
    let ranOnce = false;

    const runInitialCaptcha = async () => {
      if (cancelled || ranOnce) return;
      // eslint-disable-next-line no-console
      console.log("[FormComponent] initial v3 verify starting…");
      try {
        const token = await getV3Token(RECAPTCHA_ACTION);
        if (cancelled || ranOnce) return;

        const result = await recaptchaRepository.verify({
          version: "v3",
          token,
          action: RECAPTCHA_ACTION,
        });
        ranOnce = true;
        if (cancelled) return;

        // eslint-disable-next-line no-console
        console.log("[FormComponent] initial verify result:", result);

        const wrapperSuccess = result?.success === true;
        const innerV2Required = result?.data?.v2Required === true;
        const innerSuccess = result?.data?.success === true;

        if (wrapperSuccess && innerSuccess && !innerV2Required) {
          setIsVerified(true);
          setShowV2(false);
        } else {
          setShowV2(true);
          setIsVerified(false);
        }
        setCaptchaReady(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[FormComponent] Initial reCAPTCHA verify failed:", err);
        if (!cancelled) {
          setShowV2(true);
          setCaptchaReady(true);
        }
      }
    };

    if (v3Ready) {
      runInitialCaptcha();
    } else {
      // Wait briefly for v3 script/provider to mount, then retry once.
      const t = setTimeout(() => {
        if (!cancelled) runInitialCaptcha();
      }, 500);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v3Ready]);

  const validate = () => {
    let valid = true;

    if (!emailValidation.test(email)) {
      setEmailError("Invalid email address");
      valid = false;
    } else {
      setEmailError("");
    }

    if (!phoneValidation.test(phone)) {
      setPhoneError("Invalid phone number");
      valid = false;
    } else {
      setPhoneError("");
    }

    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return; // Stops form submission if validation fails
    }

    if (!isVerified) {
      toast.error("Please complete the captcha verification before submitting.");
      return;
    }
    if (isVerifying) return;

    setButton(false);
    setCaptchaV2Token("");

    let captchaToken;
    let captchaVersion;

    if (showV2) {
      if (!captchaV2Token) {
        setErrors((prev) => ({
          ...prev,
          captchaV2: "Please verify you are not a robot.",
        }));
        setButton(true);
        return;
      }
      captchaToken = captchaV2Token;
      captchaVersion = "v2";
    } else {
      try {
        captchaToken = await getV3Token("enquiry_submit");
        captchaVersion = "v3";
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to obtain v3 token on submit:", err);
        toast.error("Captcha not ready, please try again.");
        setButton(true);
        return;
      }
    }

    const formData = {
      email,
      subject,
      message: body,
      State: state,
      Country: country,
      City: city,
      mobile: phone,
      nameUser: name,
      entity: "equity",
      captchaToken,
      captchaVersion,
    };

    try {
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-11496349823/wuRHCO7it_cZEP_A8ekq",
        });
        // eslint-disable-next-line no-console
        console.log("Google Ads Conversion triggered");
      } else {
        // eslint-disable-next-line no-console
        console.warn("gtag is not loaded yet");
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error in gtag:", error);
    }

    try {
      const response = await fetch(apiendpoints.enquiryUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          entity: "equity",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // eslint-disable-next-line no-console
        console.error("Failed to send mail:", response.status, errorText);
        toast.error("Failed to send mail");
      }

      setButton(true);
      setThank(true);
      toast.success("Thanks for contacting us!");

      setEmail("");
      setSubject("Equity Research Mart - Enquiry");
      setBody("");
      setPhone("");
      setName("");
      setCountry("");
      setState("");
      setCity("");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error sending mail:", error);
      toast.error("Failed to send mail");
      setButton(true);
    }
  };

  return (
    <div className="relative z-10 w-full md:w-1/2 bg-white/20 md:p-6 p-2 rounded-lg shadow-lg backdrop-blur-md">
      <h2 className="text-white text-xl font-semibold mb-4">
        Get Started Today!
      </h2>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Your Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="p-2 rounded-md bg-white/80 text-gray-800 outline-none"
          required
        />
        <input
          type="email"
          placeholder="Your Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="p-2 rounded-md bg-white/80 text-gray-800 outline-none"
          required
        />
        {emailError && <p className="text-red-500 text-sm">{emailError}</p>}
        <input
          type="tel"
          placeholder="Your Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="p-2 rounded-md bg-white/80 text-gray-800 outline-none"
          required
        />
        {phoneError && <p className="text-red-500 text-sm">{phoneError}</p>}

        <input
          type="text"
          placeholder="Your City"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="p-2 rounded-md bg-white/80 text-gray-800 outline-none"
          required
        />

        {/* v2 fallback captcha */}
        {showV2 && (
          <div className="place-self-start md:col-span-2 lg:col-span-3">
            <CaptchaV2
              ref={captchaV2Ref}
              onChange={async (token) => {
                // eslint-disable-next-line no-console
                console.log(
                  "[FormComponent] v2 captcha onChange:",
                  { present: Boolean(token) }
                );
                setCaptchaV2Token(token || "");
                setErrors((prev) => ({
                  ...prev,
                  captchaV2: token ? "" : "Please verify you are not a robot.",
                }));

                if (!token) {
                  setIsVerified(false);
                  return;
                }

                setIsVerifying(true);
                setIsVerified(false);
                try {
                  const result = await recaptchaRepository.verify({
                    version: "v2",
                    token,
                    action: "enquiry_submit",
                  });
                  // eslint-disable-next-line no-console
                  console.log("[FormComponent] v2 verify result:", result);
                  const wrapperOk = result?.success === true;
                  const innerOk = result?.data?.success === true;
                  if (wrapperOk && innerOk) {
                    setIsVerified(true);
                    setErrors((prev) => ({ ...prev, captchaV2: "" }));
                  } else {
                    setIsVerified(false);
                    setErrors((prev) => ({
                      ...prev,
                      captchaV2:
                        result?.message ||
                        "Captcha verification failed. Please try again.",
                    }));
                    if (captchaV2Ref.current?.reset) {
                      captchaV2Ref.current.reset();
                    }
                  }
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error("[FormComponent] v2 verify error:", err);
                  setIsVerified(false);
                  setErrors((prev) => ({
                    ...prev,
                    captchaV2: "Captcha verification failed. Please try again.",
                  }));
                } finally {
                  setIsVerifying(false);
                }
              }}
              onExpired={() => {
                // eslint-disable-next-line no-console
                console.log("[FormComponent] v2 captcha expired");
                setCaptchaV2Token("");
                setIsVerified(false);
                setErrors((prev) => ({
                  ...prev,
                  captchaV2: "Please verify you are not a robot.",
                }));
              }}
            />
            {errors.captchaV2 && (
              <p className="mt-2 text-sm text-red-500">{errors.captchaV2}</p>
            )}
          </div>
        )}

        <button
          type="submit"
          className={`bg-[#2db928] text-white py-2 rounded-md font-semibold ${
            !isVerified
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[#2db928]/80 transition-transform transform hover:scale-105"
          }`}
          disabled={!captchaReady || !isVerified || isVerifying || !buttonEnable}
        >
          {isVerifying ? "Verifying..." : !isVerified ? "Complete Captcha to Submit" : "Submit"}
        </button>
      </form>

      <ToastContainer
        autoClose={3000}
        limit={2}
        hideProgressBar={true}
        newestOnTop={true}
      ></ToastContainer>

    
    </div>
  );
};

export default FormComponent;
